import {
    Divider,
    GridContent,
    Heading,
    PageContent,
} from '@zillow/constellation';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import React from 'react';

import { SSID_INFO_KEY, SSID_INFO_URL } from '../../server/config';
import GlobalStyle from '../components/GlobalStyle';
import MlsDisclaimer from '../components/MlsDisclaimer';

// Move interfaces to a separate types file if you have multiple components using them
export interface MlsData {
    ssid: string;
    longName: string;
    disclaimer?: string;
}

export interface MlsDisclaimersProps {
    data: MlsData[] | null;
    error: string;
}

/**
 * Server-side props for the MLS disclaimers page.
 * Extends MlsDisclaimersProps with additional server-side rendering configuration.
 */
interface MlsDisclaimersServerProps extends MlsDisclaimersProps {
    /** Toggles the visual refresh theme for the page layout */
    useVisualRefreshTheme: boolean;
}

// Separate the header into its own component for better testing
export const MlsHeader: React.FC = () => (
    <GridContent
        backgroundColor="gray100"
        padding="xl"
        marginBottom="lg"
        style={{ textAlign: 'center' }}
    >
        <Heading
            level="1"
            fontType={{ default: 'h3', md_lte: 'h4', sm_lte: 'h5' }}
            fontFamily="serif"
            marginBottom="xs"
        >
            MLS and Canadian Board Disclaimers
        </Heading>
    </GridContent>
);

// Separate the disclaimer list into its own component
export const MlsDisclaimerList: React.FC<MlsDisclaimersProps> = ({
    data,
    error,
}) => {
    if (error) return <div>{error}</div>;
    if (!data) return null;

    const sortedData = data.sort((a, b) =>
        a.longName.localeCompare(b.longName)
    );
    const lastIndex = sortedData.length - 1;

    return (
        <>
            {sortedData.map((mls, i) => (
                <React.Fragment key={mls.ssid}>
                    <MlsDisclaimer mls={mls} i={i} />
                    {mls.disclaimer && i < lastIndex && (
                        <Divider marginY="lg" />
                    )}
                </React.Fragment>
            ))}
        </>
    );
};

// Main component becomes simpler and more focused
export function MlsDisclaimers({
    data,
    error,
}: MlsDisclaimersProps): JSX.Element {
    return (
        <>
            <Head>
                <title>MLS Disclaimers - Zillow</title>
            </Head>
            <GlobalStyle />
            <MlsHeader />
            <PageContent marginTop="0" style={{ maxWidth: '720px' }}>
                <GridContent marginX="auto">
                    <MlsDisclaimerList data={data} error={error} />
                </GridContent>
            </PageContent>
        </>
    );
}

// Separate the data fetching logic into a service
export async function fetchMlsData(): Promise<{
    data: MlsData[] | null;
    error: string;
}> {
    try {
        const request = await fetch(
            `${SSID_INFO_URL}v1/ssid/types/2?disclaimerType=full`,
            {
                headers: {
                    APIKEY: SSID_INFO_KEY,
                },
            }
        );

        if (!request.ok) {
            throw new Error(
                `HTTP error! status: ${request.status} - ${request.statusText}`
            );
        }

        const data = await request.json();

        if (!Array.isArray(data)) {
            throw new Error('Invalid response format: expected an array');
        }

        return { data, error: '' };
    } catch (e) {
        console.error('Error encountered loading mls disclaimers.', e);
        return {
            data: null,
            error: 'Error loading MLS Disclaimers. If this error persists, please contact Zillow Support: https://zillow.zendesk.com/hc/en-us',
        };
    }
}

/**
 * Fetches MLS disclaimer data on each request using server-side rendering.
 * Uses fetchMlsData to retrieve disclaimer information from the SSID API.
 *
 * @returns Promise containing GetServerSidePropsResult<MlsDisclaimersServerProps>
 *          with props: data (MLS disclaimer array or null), error (error message string),
 *          and useVisualRefreshTheme (boolean for theme configuration)
 */
export const getServerSideProps: GetServerSideProps<
    MlsDisclaimersServerProps
> = async () => {
    const { data, error } = await fetchMlsData();

    return {
        props: {
            data,
            error,
            useVisualRefreshTheme: true,
        },
    };
};

export default MlsDisclaimers;
